import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory2 as InventoryIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../../../../i18n';
import { BRAND_COLORS, alpha } from '../../../../../styles/theme';
import { InventoryLocation } from '../../../../../types/api';
import { LocationTreeNode } from './LocationTreeNode';

interface LocationTreePanelProps {
  locations: InventoryLocation[];
  rootLocations: InventoryLocation[];
  selectedLocationId: string | null;
  expandedIds: Set<string>;
  filteredLocationIds: Set<string> | null;
  locationSearch: string;
  onLocationSearchChange: (value: string) => void;
  onSelectLocation: (id: string) => void;
  onSelectAllItems: () => void;
  onToggle: (id: string) => void;
  onEditLocation: (location: InventoryLocation) => void;
  onDeleteLocation: (location: InventoryLocation) => void;
  onAddLocation: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}

export const LocationTreePanel: React.FC<LocationTreePanelProps> = ({
  locations,
  rootLocations,
  selectedLocationId,
  expandedIds,
  filteredLocationIds,
  locationSearch,
  onLocationSearchChange,
  onSelectLocation,
  onSelectAllItems,
  onToggle,
  onEditLocation,
  onDeleteLocation,
  onAddLocation,
  t,
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        width: 300,
        minWidth: 250,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" sx={{ color: BRAND_COLORS.forestGreen.main }}>
          {t('inventory.locations')}
        </Typography>
        <Tooltip title={t('inventory.addLocation')}>
          <IconButton
            size="small"
            onClick={onAddLocation}
            sx={{ color: BRAND_COLORS.forestGreen.main }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder={t('inventory.searchLocations')}
          value={locationSearch}
          onChange={(e) => onLocationSearchChange(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiInputBase-root': { height: 32, fontSize: '0.8125rem' } }}
        />
      </Box>
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {/* "All Items" option */}
        <ListItemButton
          selected={selectedLocationId === null}
          onClick={onSelectAllItems}
          sx={{
            py: 0.5,
            minHeight: 36,
            '&.Mui-selected': {
              backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.08),
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            <Box sx={{ width: 20 }} />
          </ListItemIcon>
          <ListItemIcon sx={{ minWidth: 28, color: BRAND_COLORS.forestGreen.main }}>
            <InventoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t('inventory.allItems')}
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </ListItemButton>

        {rootLocations.length === 0 && (
          <Typography variant="body2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
            {t('inventory.noLocations')}
          </Typography>
        )}

        <List disablePadding>
          {rootLocations.map((loc) => (
            <LocationTreeNode
              key={loc.id}
              location={loc}
              locations={locations}
              selectedId={selectedLocationId}
              expandedIds={expandedIds}
              onSelect={onSelectLocation}
              onToggle={onToggle}
              onEdit={onEditLocation}
              onDelete={onDeleteLocation}
              depth={0}
              t={t}
              filteredIds={filteredLocationIds}
            />
          ))}
        </List>
      </Box>
    </Paper>
  );
};
